import React, { useEffect, useState } from 'react';
import { Clock, Home, MapPin, X, Sparkles } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { getClientHistory } from './crmApi';

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
}

// Human-readable labels for each qualification field the AI collects.
// Ordered so buyers' three answers render before sellers' three answers.
const QUALIFICATION_LABELS = {
  goal: 'Also considering',
  timeline: 'Timeline',
  finances: 'Finances / pre-approval',
  urgency: 'Urgency / deadlines',
  location_prefs: 'Location preferences',
  must_haves: 'Must-haves / deal-breakers',
  current_housing: 'Current housing',
  agent_representation: 'Agent representation',
  motivation: 'Motivation to sell',
  pricing_flexibility: 'Pricing flexibility',
  equity: 'Equity / mortgage',
  valuation_range: 'Preliminary AVM range',
  condition: 'Home condition',
  upgrades: 'Upgrades',
  investment_strategy: 'Investment strategy',
  funding_source: 'Funding source',
  target_numbers: 'Target numbers',
  experience: 'Investing experience',
  management_plan: 'Management plan',
};
const QUALIFICATION_ORDER = [
  'urgency', 'timeline', 'goal', 'location_prefs', 'must_haves',
  'finances', 'funding_source', 'target_numbers',
  'motivation', 'pricing_flexibility', 'equity',
  'valuation_range', 'condition', 'upgrades',
  'investment_strategy', 'experience', 'management_plan',
  'current_housing', 'agent_representation',
];

function QualificationSection({ qualification }) {
  if (!qualification || Object.keys(qualification).length === 0) return null;
  const answered = QUALIFICATION_ORDER.filter(
    (k) => qualification[k] && String(qualification[k]).trim(),
  );
  if (answered.length === 0) return null;
  const type = qualification.type;
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4"
         data-testid="client-history-qualification">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-emerald-700" />
        <div className="text-sm font-semibold text-emerald-900">
          AI qualification
        </div>
        {type && (
          <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-emerald-200/70 text-emerald-900">
            {type}
          </span>
        )}
        {qualification.updated_at && (
          <span className="text-[11px] text-emerald-800/70 ml-auto">
            captured {fmt(qualification.updated_at)}
          </span>
        )}
      </div>
      <dl className="space-y-1.5">
        {answered.map((k) => (
          <div key={k} className="grid grid-cols-[8rem_1fr] gap-2 text-sm">
            <dt className="text-emerald-800/80 text-xs uppercase tracking-wide pt-0.5">
              {QUALIFICATION_LABELS[k]}
            </dt>
            <dd className="text-emerald-950">{qualification[k]}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function TypeHistorySection({ history }) {
  if (!history || history.length === 0) return null;
  const TYPE_COLOR = {
    buyer:    'bg-emerald-100 text-emerald-800',
    seller:   'bg-amber-100 text-amber-800',
    investor: 'bg-violet-100 text-violet-800',
  };
  const SOURCE_LABEL = {
    ai_qualification: 'AI qualification',
    ai_valuation:     'AI valuation call',
    ai_voice_call:    'AI call',
    manual_edit:      'Manual edit',
  };
  return (
    <div className="rounded-lg border border-brand-cream bg-white p-4 mt-4"
         data-testid="client-history-type-flips">
      <div className="flex items-center gap-2 mb-2">
        <div className="text-sm font-semibold text-brand-green">Type transitions</div>
        <span className="text-[11px] text-brand-green/60">({history.length})</span>
      </div>
      <ul className="space-y-2">
        {history.slice().reverse().map((flip, i) => (
          <li key={i} className="text-xs flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full font-semibold ${TYPE_COLOR[flip.from] || 'bg-gray-100 text-gray-700'}`}>
              {flip.from}
            </span>
            <span className="text-brand-green/50">→</span>
            <span className={`px-2 py-0.5 rounded-full font-semibold ${TYPE_COLOR[flip.to] || 'bg-gray-100 text-gray-700'}`}>
              {flip.to}
            </span>
            <span className="text-brand-green/60">
              {fmt(flip.at)}
              {flip.source && ` · ${SOURCE_LABEL[flip.source] || flip.source}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AppointmentItem({ appt, canceled }) {
  return (
    <li data-testid={`hist-appt-${appt.id}`}
        className={`bg-brand-cream/40 rounded-lg p-3 ${canceled ? 'opacity-70' : ''}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`font-medium text-brand-green ${canceled ? 'line-through decoration-red-500/70' : ''}`}>
          {appt.title || 'Appointment'}
        </span>
        {canceled && (
          <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
            Canceled
          </span>
        )}
      </div>
      <div className="text-xs text-brand-green/70 inline-flex items-center gap-1 mt-1">
        <Clock className="w-3 h-3" /> {fmt(appt.start)} · {appt.duration_min} min
      </div>
      {appt.address && (
        <div className="text-xs text-brand-green/60 inline-flex items-center gap-1 mt-0.5">
          <MapPin className="w-3 h-3" /> {appt.address}
        </div>
      )}
    </li>
  );
}

function AppointmentList({ rows, canceled, emptyText }) {
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-brand-green/60 py-6 text-center">{emptyText}</p>;
  }
  return (
    <ul className="space-y-2 mt-2">
      {rows.map((a) => <AppointmentItem key={a.id} appt={a} canceled={canceled} />)}
    </ul>
  );
}

function InterestedListing({ item }) {
  const listing = item.listing;
  return (
    <li data-testid={`hist-interested-${item.listing_id || item.address}`}
        className="bg-brand-cream/40 rounded-lg p-3 flex items-center gap-3">
      {listing?.image_url ? (
        <img src={listing.image_url} alt="" className="w-12 h-12 rounded object-cover" />
      ) : (
        <div className="w-12 h-12 rounded bg-brand-cream flex items-center justify-center">
          <Home className="w-5 h-5 text-brand-green/60" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-brand-green truncate">{item.address || '—'}</div>
        <div className="text-xs text-brand-green/60">
          {listing?.price ? `${listing.price} · ` : ''}
          {listing?.beds ? `${listing.beds} bd` : ''}
          {listing?.baths ? ` · ${listing.baths} ba` : ''}
          {item.added_at ? ` · added ${fmt(item.added_at)}` : ''}
        </div>
      </div>
    </li>
  );
}

export default function ClientHistoryDialog({ clientId, clientName, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !clientId) { setData(null); setError(''); return; }
    let alive = true;
    setLoading(true);
    setError('');
    getClientHistory(clientId)
      .then((body) => { if (alive) setData(body); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Failed to load history'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, clientId]);

  const counts = data?.appointments?.counts || { upcoming: 0, past: 0, canceled: 0 };
  const interested = data?.interested_listings || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="client-history-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {clientName || 'Client'} <span className="text-sm font-normal text-brand-green/60">— history</span>
          </DialogTitle>
          <DialogDescription>
            Every appointment and property inquiry tied to this caller's phone number.
          </DialogDescription>
        </DialogHeader>

        {loading && <p className="text-sm text-brand-green/60 py-8 text-center">Loading…</p>}
        {error && (
          <div className="text-sm text-red-700 bg-red-50 rounded p-3 flex items-center gap-2">
            <X className="w-4 h-4" /> {error}
          </div>
        )}

        {data && (
          <>
            <QualificationSection qualification={data.client?.qualification} />

            <TypeHistorySection history={data.client?.type_history} />

            <div className="mb-3 mt-4">
              <div className="text-xs uppercase tracking-wide text-brand-green/60 mb-1">
                Interested properties ({interested.length})
              </div>
              {interested.length === 0 ? (
                <p className="text-sm text-brand-green/60">
                  The AI hasn't logged any property inquiries from this caller yet.
                </p>
              ) : (
                <ul className="space-y-2" data-testid="client-history-interested">
                  {interested.map((i, ix) => (
                    <InterestedListing key={i.listing_id || i.address || ix} item={i} />
                  ))}
                </ul>
              )}
            </div>

            <Tabs defaultValue="upcoming" className="mt-4">
              <TabsList data-testid="client-history-tabs">
                <TabsTrigger value="upcoming" data-testid="client-history-tab-upcoming">
                  Upcoming ({counts.upcoming})
                </TabsTrigger>
                <TabsTrigger value="past" data-testid="client-history-tab-past">
                  Past ({counts.past})
                </TabsTrigger>
                <TabsTrigger value="canceled" data-testid="client-history-tab-canceled">
                  Canceled ({counts.canceled})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="upcoming">
                <AppointmentList rows={data.appointments.upcoming}
                                 emptyText="Nothing on the schedule with this client." />
              </TabsContent>
              <TabsContent value="past">
                <AppointmentList rows={data.appointments.past}
                                 emptyText="No past appointments yet." />
              </TabsContent>
              <TabsContent value="canceled">
                <AppointmentList rows={data.appointments.canceled} canceled
                                 emptyText="No canceled appointments." />
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
